import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  ClearOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlaySquareOutlined,
  PlusOutlined,
  SaveOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Table,
  Tag,
} from "antd";

import isEmpty from "../validations/is-empty";
import apiClient from "../actions/api";
import openNotification from "../components/Notifications/openNotification";

const { TextArea } = Input;

const JobPosting = ({ title }) => {
  document.title = title;

  const themeMode = useSelector((state) => state.themeMode);

  const [allData, setAllData] = useState([]);
  const [searchedData, setSearchedData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalFlag, setModalFlag] = useState("add");
  const [modalItemID, setModalItemID] = useState("");

  // modal state
  const [mTitle, setMTitle] = useState("");
  const [mLocation, setMLocation] = useState("");
  const [mEmploymentType, setMEmploymentType] = useState("Full-time");
  const [mDepartment, setMDepartment] = useState("Software Development");
  const [mSummary, setMSummary] = useState("");
  const [mDescription, setMDescription] = useState("");
  const [mResponsibilities, setMResponsibilities] = useState("");
  const [mRequirements, setMRequirements] = useState("");
  const [mOfferDetails, setMOfferDetails] = useState("");
  const [mStatus, setMStatus] = useState(1);
  const [mBadges, setMBadges] = useState([]);

  const columns = [
    {
      title: "#",
      key: "no",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
    },
    {
      title: "Employment Type",
      dataIndex: "employmentType",
      key: "employmentType",
      render: (text) => <Tag color="red">{text}</Tag>,
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
      render: (text) => <Tag color="green">{text}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) =>
        text === 1 ? (
          <Tag color="rgb(0, 194, 204)">Published</Tag>
        ) : (
          <Tag color="rgb(204, 0, 0)">Unpublished</Tag>
        ),
    },
    {
      title: "Slug",
      dataIndex: "slug",
      key: "slug",
    },
    {
      title: "Badges",
      dataIndex: "badges",
      key: "badges",
      render: (text) =>
        text.map((item) => {
          return <Tag color="gold">{item}</Tag>;
        }),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "row", gap: 10 }}>
          <Button type="default" onClick={() => handleViewDetail(record)}>
            <EyeOutlined />
          </Button>
          <Button type="primary" onClick={() => handleItemEdit(record)}>
            <EditOutlined />
          </Button>
          <Popconfirm
            title="Delete this resume"
            description="Are you sure to delete this resume?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleItemDelete(record._id)}
          >
            <Button type="primary" danger>
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const setModalItems = (item) => {
    setMTitle(item.title);
    setMLocation(item.location);
    setMEmploymentType(item.employmentType);
    setMDepartment(item.department);
    setMSummary(item.summary);
    setMDescription(item.description);
    setMResponsibilities(item.responsibilities);
    setMRequirements(item.requirements);
    setMOfferDetails(item.offerDetails);
    setMStatus(item.status);
    setMBadges(item.badges);
  };

  const handleViewDetail = (item) => {
    setModalItems(item);
    setOpen(true);
    setModalFlag("view");
  };

  const handleItemEdit = (item) => {
    setModalItems(item);
    setOpen(true);
    setModalFlag("edit");
    setModalItemID(item._id);
  };

  const handleItemDelete = async (id) => {
    setModalItemID(id);

    const params = {
      id,
    };

    setModalLoading(true);

    await apiClient
      .post("/candidates/delete-job", params)
      .then(async (res) => {
        if (res.data?.status) {
          openNotification("success", "Success", res.data.msg);
        }
        handleModalCancel();
        setModalLoading(false);
        getData();
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setModalLoading(false);
      });
  };

  const onOpenAddModal = () => {
    setOpen(true);
  };

  const handleJobSave = async () => {
    if (modalFlag === "edit" && isEmpty(modalItemID)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please choose job correctly!",
      );
      return;
    }

    if (isEmpty(mTitle)) {
      openNotification("error", "Validation Error!", "Please type Title.");
      return;
    }

    if (isEmpty(mLocation)) {
      openNotification("error", "Validation Error!", "Please type Location.");
      return;
    }

    if (isEmpty(mEmploymentType)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please choose Emplyment Type.",
      );
      return;
    }

    if (isEmpty(mDepartment)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please choose Department.",
      );
      return;
    }

    if (isEmpty(mSummary)) {
      openNotification("error", "Validation Error!", "Please type Summary.");
      return;
    }

    if (isEmpty(mDescription)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please type Description.",
      );
      return;
    }

    if (isEmpty(mResponsibilities)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please type Responsibilities.",
      );
      return;
    }

    if (isEmpty(mRequirements)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please type Requirements.",
      );
      return;
    }

    if (isEmpty(mOfferDetails)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please type Offter Details.",
      );
      return;
    }

    if (isEmpty(mStatus)) {
      openNotification("error", "Validation Error!", "Please choose Status.");
      return;
    }

    if (isEmpty(mBadges)) {
      openNotification("error", "Validation Error!", "Please choose Badges.");
      return;
    }

    const params = {
      title: mTitle,
      location: mLocation,
      employmentType: mEmploymentType,
      department: mDepartment,
      summary: mSummary,
      description: mDescription,
      responsibilities: mResponsibilities,
      requirements: mRequirements,
      offerDetails: mOfferDetails,
      status: mStatus,
      badges: mBadges,
    };

    setModalLoading(true);

    const payload =
      modalFlag === "edit" ? { ...params, id: modalItemID } : params;

    await apiClient
      .post(
        modalFlag === "edit" ? "/candidates/edit-job" : "/candidates/add-job",
        payload,
      )
      .then(async (res) => {
        if (res.data?.status) {
          openNotification("success", "Success", res.data.msg);
        }
        handleModalCancel();
        setModalLoading(false);
        getData();
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setModalLoading(false);
      });
  };

  const handleModalCancel = () => {
    setOpen(false);
    // initialize
    handleJobClear();
  };

  const handleJobClear = () => {
    // initialize
    setMTitle("");
    setMLocation("");
    setMEmploymentType("Full-time");
    setMDepartment("Software Development");
    setMSummary("");
    setMDescription("");
    setMResponsibilities("");
    setMRequirements("");
    setMOfferDetails("");
    setMStatus(1);
    setMBadges([]);
    setModalFlag("add");
    setModalItemID("");
  };

  const getData = async () => {
    setLoading(true);

    await apiClient
      .get("/candidates/get-jobs")
      .then((res) => {
        if (isEmpty(res.data?.result)) {
          setAllData([]);
          setSearchedData([]);
        } else {
          setAllData(res.data?.result);
          setSearchedData(res.data?.result);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (!isEmpty(searchText)) {
      const filteredData = allData.filter((item) =>
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(searchText.toLowerCase()),
      );

      setSearchedData(filteredData);
    } else {
      setSearchedData(allData);
    }
  }, [searchText]);

  return (
    <div
      className={`dashboardSection management-page ${themeMode.themeMode ? "dashboard-dark" : ""}`}
    >
      <div className="dashboard-heading">
        <div>
          <h2 className="title">
            <span className="title-icon">
              <PlaySquareOutlined />
            </span>
            Job Posting
          </h2>
          <p>Create, publish, and manage open positions.</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={loading}
          onClick={onOpenAddModal}
        >
          New job post
        </Button>
      </div>
      <Card className="search-section" variant="borderless">
        <div className="filter-heading">
          <div>
            <SearchOutlined /> Search jobs
          </div>
          <span>Filter positions by any job detail</span>
        </div>
        <div className="filter-grid job-filter-grid">
          <div className="field">
            <div className="label">Keywords</div>
            <Input
              prefix={<SearchOutlined className="input-icon" />}
              placeholder="e.g. Frontend Engineer"
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
            />
          </div>
          <div className="job-result-count">
            <span>{searchedData.length}</span> matching positions
          </div>
        </div>
      </Card>
      <Card className="table-section" variant="borderless">
        <div className="table-heading">
          <div>
            <h3>Open positions</h3>
            <span>Manage publishing status and job details</span>
          </div>
          <Tag>{searchedData.length} records</Tag>
        </div>
        <Table
          columns={columns}
          dataSource={searchedData}
          loading={loading}
          size="middle"
          scroll={{ x: 1050 }}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => `${total} job posts`,
          }}
        />
      </Card>

      <Modal
        title={
          modalFlag === "add"
            ? "Job Post"
            : modalFlag === "View"
              ? "View Job"
              : "Edit Job"
        }
        width={1000}
        open={open}
        centered
        className="dashboard-edit-modal job-post-modal"
        onOk={handleJobSave}
        onCancel={handleModalCancel}
        footer={[
          <Button
            key="submit"
            type="primary"
            loading={modalLoading}
            onClick={handleJobSave}
            icon={<SaveOutlined />}
            disabled={modalFlag === "view" ? true : false}
          >
            {modalFlag === "add"
              ? "SAVE"
              : modalFlag === "View"
                ? "DISABLED"
                : "EDIT"}
          </Button>,
          <Button
            type="default"
            loading={modalLoading}
            onClick={handleJobClear}
            icon={<ClearOutlined />}
            disabled={modalFlag === "view" ? true : false}
          >
            CLEAR
          </Button>,
        ]}
      >
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col className="gutter-row" xs={24} md={12}>
            <div className="modal-field">
              <label className="label">Title</label>
              <Input
                value={mTitle}
                onChange={(e) => setMTitle(e.target.value)}
              />
            </div>
          </Col>
          <Col className="gutter-row" xs={24} md={12}>
            <div className="modal-field">
              <label className="label">Location</label>
              <Input
                value={mLocation}
                onChange={(e) => setMLocation(e.target.value)}
              />
            </div>
          </Col>
          <Col className="gutter-row" xs={24} md={12}>
            <div className="modal-field">
              <label className="label">Employment Type</label>
              <Select
                value={mEmploymentType}
                onChange={(value) => setMEmploymentType(value)}
                style={{ width: "100%" }}
                options={[
                  {
                    value: "Full-time",
                    label: "Full-time",
                  },
                  {
                    value: "Part-time",
                    label: "Part-time",
                  },
                  {
                    value: "Contract",
                    label: "Contract",
                  },
                ]}
              />
            </div>
          </Col>
          <Col className="gutter-row" xs={24} md={12}>
            <div className="modal-field">
              <label className="label">Department</label>
              <Select
                value={mDepartment}
                onChange={(value) => setMDepartment(value)}
                style={{ width: "100%" }}
                options={[
                  {
                    value: "Software Development",
                    label: "Software Development",
                  },
                  {
                    value: "Data Engineering",
                    label: "Data Engineering",
                  },
                  {
                    value: "Product Management",
                    lable: "Product Management",
                  },
                  {
                    value: "Quality Assurance (QA)",
                    lable: "Quality Assurance (QA)",
                  },
                  {
                    value: "DevOps/SRE",
                    lable: "DevOps/SRE",
                  },
                  {
                    value: "UX/UI Design",
                    lable: "UX/UI Design",
                  },
                  {
                    value: "Technical Support/Documentation",
                    lable: "Technical Support/Documentation",
                  },
                ]}
              />
            </div>
          </Col>
          <Col className="gutter-row" span={24}>
            <div className="modal-field">
              <label className="label">Summary</label>
              <TextArea
                rows={4}
                value={mSummary}
                onChange={(e) => setMSummary(e.target.value)}
              />
            </div>
          </Col>
          <Col className="gutter-row" span={24}>
            <div className="modal-field">
              <label className="label">Description</label>
              <TextArea
                rows={4}
                value={mDescription}
                onChange={(e) => setMDescription(e.target.value)}
              />
            </div>
          </Col>
          <Col className="gutter-row" span={24}>
            <div className="modal-field">
              <label className="label">Responsibilities</label>
              <TextArea
                rows={4}
                value={mResponsibilities}
                onChange={(e) => setMResponsibilities(e.target.value)}
              />
            </div>
          </Col>
          <Col className="gutter-row" span={24}>
            <div className="modal-field">
              <label className="label">Requirements</label>
              <TextArea
                rows={4}
                value={mRequirements}
                onChange={(e) => setMRequirements(e.target.value)}
              />
            </div>
          </Col>
          <Col className="gutter-row" span={24}>
            <div className="modal-field">
              <label className="label">Offer Details</label>
              <TextArea
                rows={4}
                value={mOfferDetails}
                onChange={(e) => setMOfferDetails(e.target.value)}
              />
            </div>
          </Col>
          <Col className="gutter-row" xs={24} md={12}>
            <div className="modal-field">
              <label className="label">Status</label>
              <Select
                value={mStatus}
                onChange={(value) => setMStatus(value)}
                style={{ width: "100%" }}
                options={[
                  {
                    value: 1,
                    label: "Published",
                  },
                  {
                    value: 0,
                    label: "Unpublished",
                  },
                ]}
              />
            </div>
          </Col>
          <Col className="gutter-row" xs={24} md={12}>
            <div className="modal-field">
              <label className="label">Badges</label>
              <Select
                mode="multiple"
                value={mBadges}
                onChange={(value) => setMBadges(value)}
                style={{ width: "100%" }}
                options={[
                  {
                    value: "Full-time",
                    label: "Full-time",
                  },
                  {
                    value: "Remote-Global",
                    label: "Remote-Global",
                  },
                  {
                    value: "Data Engineering",
                    label: "Data Engineering",
                  },
                  {
                    value: "Remote-US",
                    label: "Remote-US",
                  },
                  {
                    value: "Software Development",
                    label: "Software Development",
                  },
                ]}
              />
            </div>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default JobPosting;
